<script setup>
import { ref, computed } from "vue";
import { useGlobalStore } from "@stores/global.js";
import { useShopifyStore } from "@stores/shopify.js";
import gsap from "gsap";
import { prefersReducedMotion } from "@utils/prefersReducedMotion";
import Button from "@components/global/Button.vue";

const global = useGlobalStore();
const shopify = useShopifyStore();
const menu = ref(null);

const headerCtaCardUrl = computed(() => {
	if (!shopify.headerCtaCard) return null;
	if (shopify.headerCtaCard.collection) {
		return {
			name: "collection",
			params: { slug: shopify.headerCtaCard.collection.slug },
		};
	}
	if (shopify.headerCtaCard.product) {
		return {
			name: "product",
			params: { slug: shopify.headerCtaCard.product.slug },
		};
	}
	return null;
});

const menuMaxHeight = computed(() => {
	if (global.announcementBarVisible) {
		return "calc(100dvh - var(--header-height) - var(--announcement-bar-height))";
	}
	return "calc(100dvh - var(--header-height))";
});

const mainLinks = ref([
	{
		label: "Home",
		to: "index",
	},
	{
		label: "About",
		to: "about",
	},
	{
		label: "Contact Us",
		href: "https://contact.gorgias.help/forms/n9ff37rb",
	},
]);

const onEnter = (el, done) => {
	if (prefersReducedMotion()) return done();

	const container = el.querySelector("[animate-menu-container]");
	const tl = gsap.timeline({
		onComplete: () => {
			done();
		},
	});

	gsap.set(el, {
		scaleY: 0,
	});
	gsap.set(container, {
		opacity: 0,
		y: -32,
	});

	tl.to(el, {
		scaleY: 1,
		duration: 0.35,
		ease: "power2.inOut",
	});
	tl.to(container, {
		opacity: 1,
		y: 0,
		duration: 0.22,
		ease: "power2.out",
	});
};

const onLeave = (el, done) => {
	if (prefersReducedMotion()) return done();
	const container = el.querySelector("[animate-menu-container]");
	const tl = gsap.timeline({
		onComplete: () => {
			done();
		},
	});

	tl.to(container, {
		opacity: 0,
		y: 32,
		duration: 0.22,
		ease: "power2.in",
	});
	tl.to(
		el,
		{
			scaleY: 0,
			duration: 0.22,
			ease: "power2.in",
		},
		"-=0.1",
	);
};
</script>

<template>
	<transition appear :css="false" @enter="onEnter" @leave="onLeave">
		<div
			v-if="global.mobile"
			class="bg-theme absolute top-(--header-height) left-0 w-full origin-top overflow-y-auto pb-32 text-(--theme-surface) transition-colors"
			:style="{ maxHeight: menuMaxHeight }"
			ref="menu"
		>
			<div class="container" animate-menu-container>
				<div class="grid grid-cols-12 gap-24">
					<div
						class="col-span-12 flex flex-col items-start gap-24 lg:col-span-2 xl:col-span-3"
					>
						<p
							class="body-sm text-(--theme-surface) uppercase opacity-50 transition-colors"
						>
							Sitemap
						</p>
						<ul class="body-md flex flex-col gap-16">
							<li v-for="link in mainLinks" :key="link.label">
								<a
									v-if="link.href"
									:href="link.href"
									target="_blank"
									rel="noopener noreferrer"
									@click="global.toggleMobile(false)"
								>
									{{ link.label }}
								</a>
								<router-link
									v-else
									:to="{ name: link.to }"
									@click="global.toggleMobile(false)"
								>
									{{ link.label }}
								</router-link>
							</li>
						</ul>
						<Button
							:to="{ name: 'teams' }"
							class="mt-auto w-full lg:w-auto"
							@click="global.toggleMobile(false)"
							>Shop all</Button
						>
					</div>
					<div
						class="col-span-12 flex flex-col gap-24 lg:col-span-5 xl:col-span-4"
					>
						<p
							class="body-sm text-(--theme-surface) uppercase opacity-50 transition-colors"
						>
							Collections
						</p>
						<ul class="body-md grid grid-cols-2 gap-16">
							<li
								v-for="collection in shopify.teamsCollections"
								:key="collection.id"
							>
								<router-link
									:to="{
										name: 'collection',
										params: { slug: collection.slug },
									}"
									@click="global.toggleMobile(false)"
									class="flex items-center gap-8"
								>
									<img
										:src="collection.icon"
										:alt="collection.icon_alt || collection.title"
										class="size-20 object-contain"
										v-if="collection.icon"
									/>
									<span>{{ collection.title }}</span>
								</router-link>
							</li>
						</ul>
					</div>

					<div class="col-span-12 lg:col-span-5 xl:pl-24">
						<div
							v-if="shopify.headerCtaCard"
							class="bg-theme-surface relative isolate flex aspect-square items-end overflow-hidden rounded-lg p-16 before:absolute before:inset-0 before:-z-1 before:size-full before:bg-linear-to-t before:from-black/60 before:to-transparent md:aspect-video md:p-24"
						>
							<img
								v-if="shopify.headerCtaCard.image"
								:src="shopify.headerCtaCard.image"
								:alt="
									shopify.headerCtaCard.image_alt ||
									shopify.headerCtaCard.title
								"
								class="absolute inset-0 -z-2 h-full w-full object-cover"
							/>
							<div class="flex flex-col items-start gap-16 md:gap-24">
								<div
									class="text-text-inverted flex flex-col gap-8 md:gap-12"
								>
									<p
										v-if="shopify.headerCtaCard.title"
										class="h3 font-primary-condensed"
									>
										{{ shopify.headerCtaCard.title }}
									</p>
									<p
										v-if="shopify.headerCtaCard.description"
										class="body-md"
									>
										{{ shopify.headerCtaCard.description }}
									</p>
								</div>
								<Button
									class="box-link -neutral-background"
									v-if="headerCtaCardUrl"
									:to="headerCtaCardUrl"
									@click="global.toggleMobile(false)"
								>
									{{ shopify.headerCtaCard.button_text }}
								</Button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>
